import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { FiPlus, FiEye, FiSend, FiCheck } from 'react-icons/fi';

const TABS = ['Scheme', 'Periods', 'Returns', 'Payments'];

const BOX_LABELS = [
  ['box1VatDueOutput', 'Box 1 — VAT due on sales'],
  ['box2VatDueEcAcquisitions', 'Box 2 — VAT due on EC acquisitions'],
  ['box3TotalVatDue', 'Box 3 — Total VAT due'],
  ['box4VatReclaimedInput', 'Box 4 — VAT reclaimed on purchases'],
  ['box5NetVatDue', 'Box 5 — Net VAT due'],
  ['box6TotalSalesExVat', 'Box 6 — Total sales (ex VAT)'],
  ['box7TotalPurchasesExVat', 'Box 7 — Total purchases (ex VAT)'],
  ['box8TotalEcSales', 'Box 8 — Total EC sales'],
  ['box9TotalEcAcquisitions', 'Box 9 — Total EC acquisitions'],
];

const Vat = () => {
  const { success, error: showError } = useToast();
  const [tab, setTab] = useState('Scheme');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const [currentScheme, setCurrentScheme] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [returns, setReturns] = useState([]);
  const [payments, setPayments] = useState([]);
  const [taxRegime, setTaxRegime] = useState(null);
  const [currencySymbol, setCurrencySymbol] = useState('£');

  const [schemeForm, setSchemeForm] = useState({ schemeName: 'standard', flatRatePercentage: '', effectiveFrom: new Date().toISOString().slice(0, 10) });
  const [periodForm, setPeriodForm] = useState({ startDate: '', endDate: '', dueDate: '' });
  const [paymentForm, setPaymentForm] = useState({ vatReturnId: '', amount: '', direction: 'payment', paidAt: new Date().toISOString().slice(0, 10) });

  const [selectedReturn, setSelectedReturn] = useState(null);
  const [preview, setPreview] = useState(null);

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/vat/schemes/current').catch(() => ({ data: { data: null } })),
      apiClient.get('/vat/periods'),
      apiClient.get('/vat/returns'),
      apiClient.get('/vat/payments'),
      apiClient.get('/vat/returns/tax-regime').catch(() => ({ data: { data: null } })),
      apiClient.get('/organisations/current'),
      apiClient.get('/lookups/currencies'),
    ]).then(([schemeRes, periodsRes, returnsRes, paymentsRes, regimeRes, orgRes, currRes]) => {
      setCurrentScheme(schemeRes.data.data);
      setPeriods(periodsRes.data.data);
      setReturns(returnsRes.data.data);
      setPayments(paymentsRes.data.data);
      setTaxRegime(regimeRes.data.data);
      const org = orgRes.data.data;
      const currencies = currRes.data.data;
      const symbol = currencies.find((c) => c.id === org.baseCurrencyId)?.symbol || '£';
      setCurrencySymbol(symbol);
    }).catch((err) => showError(getErrorMessage(err, 'Could not load VAT data')))
      .finally(() => setLoading(false));
  };

  useEffect(loadAll, []); // eslint-disable-line react-hooks/exhaustive-deps

  const periodLabel = (id) => {
    const p = periods.find((x) => x.id === id);
    return p ? `${new Date(p.startDate).toLocaleDateString()} – ${new Date(p.endDate).toLocaleDateString()}` : id;
  };

  // ---- Scheme ----
  const handleCreateScheme = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...schemeForm };
      if (!payload.flatRatePercentage) delete payload.flatRatePercentage;
      await apiClient.post('/vat/schemes', payload);
      success('✅ VAT scheme saved');
      setShowForm(false);
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not save VAT scheme')); }
  };

  // ---- Periods ----
  const handleCreatePeriod = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/vat/periods', periodForm);
      success('✅ VAT period created');
      setShowForm(false);
      setPeriodForm({ startDate: '', endDate: '', dueDate: '' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not create period')); }
  };

  const previewBoxes = async (periodId) => {
    setPreview(null);
    try {
      if (taxRegime?.kind === 'us_sales_tax') {
        const { data } = await apiClient.get('/vat/returns/preview/sales-tax-summary', { params: { vatPeriodId: periodId } });
        setPreview({ periodId, kind: 'us_sales_tax', data: data.data });
      } else if (taxRegime?.kind === 'generic_vat') {
        const { data } = await apiClient.get('/vat/returns/preview/generic-summary', { params: { vatPeriodId: periodId } });
        setPreview({ periodId, kind: 'generic_vat', data: data.data });
      } else {
        const { data } = await apiClient.get('/vat/returns/preview/boxes', { params: { vatPeriodId: periodId } });
        setPreview({ periodId, kind: 'uk_vat', boxes: data.data });
      }
    } catch (err) { showError(getErrorMessage(err, 'Could not preview VAT')); }
  };

  const handlePrepareReturn = async (periodId) => {
    try {
      await apiClient.post('/vat/returns/prepare', { vatPeriodId: periodId });
      success('✅ VAT return prepared — check the Returns tab');
      setPreview(null);
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not prepare return')); }
  };

  // ---- Returns ----
  const viewReturn = (r) => setSelectedReturn(r);

  const handleReviewReturn = async (returnId) => {
    try {
      await apiClient.post(`/vat/returns/${returnId}/review`);
      success('✅ Return marked reviewed');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not review return')); }
  };

  const handleSubmitReturn = async (returnId) => {
    if (!window.confirm('Submit this VAT return? This is a final, formal action.')) return;
    try {
      await apiClient.post(`/vat/returns/${returnId}/submit`);
      success('✅ Return submitted');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not submit return')); }
  };

  // ---- Payments ----
  const handleRecordPayment = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/vat/payments', paymentForm);
      success('✅ VAT payment recorded');
      setShowForm(false);
      setPaymentForm({ vatReturnId: '', amount: '', direction: 'payment', paidAt: new Date().toISOString().slice(0, 10) });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not record payment')); }
  };

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="page-header">
        <h1>VAT</h1>
        <p>Scheme, periods, returns, and tax authority payments</p>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); setShowForm(false); setSelectedReturn(null); setPreview(null); }}
            style={{
              padding: '0.75rem 1rem', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: 600, color: tab === t ? '#3b82f6' : '#6b7280',
              borderBottom: tab === t ? '2px solid #3b82f6' : '2px solid transparent',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Scheme' && (
        <section className="page-section">
          <div className="section-header">
            <h2>VAT Scheme</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : currentScheme ? 'Change Scheme' : 'Set Up Scheme'}
            </button>
          </div>
          {currentScheme ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
              <div>
                <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0 0 0.25rem 0', textTransform: 'uppercase' }}>Scheme</p>
                <p style={{ fontSize: '1rem', margin: 0, fontWeight: 500, textTransform: 'capitalize' }}>{currentScheme.schemeName?.replace('_', ' ')}</p>
              </div>
              {currentScheme.flatRatePercentage && (
                <div>
                  <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0 0 0.25rem 0', textTransform: 'uppercase' }}>Flat Rate</p>
                  <p style={{ fontSize: '1rem', margin: 0, fontWeight: 500 }}>{currentScheme.flatRatePercentage}%</p>
                </div>
              )}
              <div>
                <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0 0 0.25rem 0', textTransform: 'uppercase' }}>Effective From</p>
                <p style={{ fontSize: '1rem', margin: 0, fontWeight: 500 }}>{new Date(currentScheme.effectiveFrom).toLocaleDateString()}</p>
              </div>
            </div>
          ) : (
            <p style={{ color: '#6b7280' }}>No VAT scheme set up yet.</p>
          )}
          {showForm && (
            <form onSubmit={handleCreateScheme} className="form-grid" style={{ marginTop: '1.5rem' }}>
              <div className="form-field">
                <label>Scheme *</label>
                <select required value={schemeForm.schemeName} onChange={(e) => setSchemeForm(p => ({ ...p, schemeName: e.target.value }))}>
                  <option value="standard">Standard</option>
                  <option value="flat_rate">Flat Rate</option>
                  <option value="cash_accounting">Cash Accounting</option>
                  <option value="annual_accounting">Annual Accounting</option>
                </select>
              </div>
              {schemeForm.schemeName === 'flat_rate' && (
                <div className="form-field"><label>Flat Rate %</label><input type="number" step="0.01" value={schemeForm.flatRatePercentage} onChange={(e) => setSchemeForm(p => ({ ...p, flatRatePercentage: e.target.value }))} /></div>
              )}
              <div className="form-field"><label>Effective From *</label><input type="date" required value={schemeForm.effectiveFrom} onChange={(e) => setSchemeForm(p => ({ ...p, effectiveFrom: e.target.value }))} /></div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Save</button></div>
            </form>
          )}
        </section>
      )}

      {tab === 'Periods' && (
        <>
          <section className="page-section">
            <div className="section-header">
              <h2>VAT Periods</h2>
              <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
                <FiPlus size={14} /> {showForm ? 'Cancel' : 'New Period'}
              </button>
            </div>
            {showForm && (
              <form onSubmit={handleCreatePeriod} className="form-grid" style={{ marginBottom: '1.5rem' }}>
                <div className="form-field"><label>Start Date *</label><input type="date" required value={periodForm.startDate} onChange={(e) => setPeriodForm(p => ({ ...p, startDate: e.target.value }))} /></div>
                <div className="form-field"><label>End Date *</label><input type="date" required value={periodForm.endDate} onChange={(e) => setPeriodForm(p => ({ ...p, endDate: e.target.value }))} /></div>
                <div className="form-field"><label>Due Date *</label><input type="date" required value={periodForm.dueDate} onChange={(e) => setPeriodForm(p => ({ ...p, dueDate: e.target.value }))} /></div>
                <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Create</button></div>
              </form>
            )}
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>Period</th><th>Due Date</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {periods.length === 0 ? (
                    <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No periods yet</td></tr>
                  ) : periods.map((p) => (
                    <tr key={p.id}>
                      <td style={{ fontWeight: 600 }}>{new Date(p.startDate).toLocaleDateString()} – {new Date(p.endDate).toLocaleDateString()}</td>
                      <td>{new Date(p.dueDate).toLocaleDateString()}</td>
                      <td><span className="badge" style={{ background: p.status === 'filed' ? '#10b981' : '#f59e0b', color: 'white' }}>{p.status}</span></td>
                      <td>
                        <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => previewBoxes(p.id)}>
                          <FiEye size={14} /> Preview
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {preview && (
            <section className="page-section">
              <div className="section-header">
                <h2>Preview — {periodLabel(preview.periodId)}</h2>
                {preview.kind === 'uk_vat' && (
                  <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => handlePrepareReturn(preview.periodId)}>
                    <FiCheck size={14} /> Prepare Return From This
                  </button>
                )}
              </div>

              {preview.kind !== 'uk_vat' && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '1rem', marginBottom: '1rem', color: '#92400e', fontSize: '0.9rem' }}>
                  ⚠️ {preview.data.label}
                </div>
              )}

              {preview.kind === 'uk_vat' && (
                <div className="table-container">
                  <table className="data-table">
                    <tbody>
                      {BOX_LABELS.map(([key, label]) => (
                        <tr key={key}>
                          <td>{label}</td>
                          <td style={{ fontWeight: 600, textAlign: 'right' }}>{currencySymbol}{Number(preview.boxes[key]).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {preview.kind === 'generic_vat' && (
                <div className="table-container">
                  <table className="data-table">
                    <tbody>
                      <tr><td>Total taxable sales</td><td style={{ fontWeight: 600, textAlign: 'right' }}>{currencySymbol}{Number(preview.data.totalTaxableSales).toFixed(2)}</td></tr>
                      <tr><td>Total taxable purchases</td><td style={{ fontWeight: 600, textAlign: 'right' }}>{currencySymbol}{Number(preview.data.totalTaxablePurchases).toFixed(2)}</td></tr>
                      <tr><td>Output tax (collected)</td><td style={{ fontWeight: 600, textAlign: 'right' }}>{currencySymbol}{Number(preview.data.totalOutputTax).toFixed(2)}</td></tr>
                      <tr><td>Input tax (paid)</td><td style={{ fontWeight: 600, textAlign: 'right' }}>{currencySymbol}{Number(preview.data.totalInputTax).toFixed(2)}</td></tr>
                      <tr><td style={{ fontWeight: 700 }}>Net due</td><td style={{ fontWeight: 700, textAlign: 'right' }}>{currencySymbol}{Number(preview.data.netDue).toFixed(2)}</td></tr>
                    </tbody>
                  </table>
                </div>
              )}

              {preview.kind === 'us_sales_tax' && (
                <div className="table-container">
                  <table className="data-table">
                    <tbody>
                      <tr><td>Total taxable sales</td><td style={{ fontWeight: 600, textAlign: 'right' }}>{currencySymbol}{Number(preview.data.totalTaxableSales).toFixed(2)}</td></tr>
                      <tr><td style={{ fontWeight: 700 }}>Total tax collected</td><td style={{ fontWeight: 700, textAlign: 'right' }}>{currencySymbol}{Number(preview.data.totalTaxCollected).toFixed(2)}</td></tr>
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </>
      )}

      {tab === 'Returns' && (
        <>
          <section className="page-section">
            <div className="section-header"><h2>VAT Returns</h2></div>
            <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '1rem' }}>
              Formal returns (with review/submit) are only available for the UK's box-based format. Other regimes' estimates are preview-only — see the Periods tab.
            </p>
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>Period</th><th>Box 5 (Net Due)</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
                  ) : returns.length === 0 ? (
                    <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No returns yet — prepare one from a period's box preview</td></tr>
                  ) : returns.map((r) => (
                    <tr key={r.id}>
                      <td style={{ fontWeight: 600 }}>{periodLabel(r.vatPeriodId)}</td>
                      <td>{currencySymbol}{Number(r.box5NetVatDue).toFixed(2)}</td>
                      <td><span className="badge" style={{ background: r.status === 'submitted' ? '#10b981' : r.status === 'reviewed' ? '#3b82f6' : '#f59e0b', color: 'white' }}>{r.status}</span></td>
                      <td>
                        <div className="actions">
                          <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => viewReturn(r)}>
                            <FiEye size={14} /> View
                          </button>
                          {r.status === 'draft' && (
                            <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleReviewReturn(r.id)}>
                              <FiCheck size={14} /> Mark Reviewed
                            </button>
                          )}
                          {r.status === 'reviewed' && (
                            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => handleSubmitReturn(r.id)}>
                              <FiSend size={14} /> Submit
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {selectedReturn && (
            <section className="page-section">
              <div className="section-header">
                <h2>Return — {periodLabel(selectedReturn.vatPeriodId)}</h2>
                <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => setSelectedReturn(null)}>Close</button>
              </div>
              <div className="table-container">
                <table className="data-table">
                  <tbody>
                    {BOX_LABELS.map(([key, label]) => (
                      <tr key={key}>
                        <td>{label}</td>
                        <td style={{ fontWeight: 600, textAlign: 'right' }}>{currencySymbol}{Number(selectedReturn[key]).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}

      {tab === 'Payments' && (
        <section className="page-section">
          <div className="section-header"><h2>Record VAT Payment / Refund</h2></div>
          <form onSubmit={handleRecordPayment} className="form-grid">
            <div className="form-field">
              <label>VAT Return *</label>
              <select required value={paymentForm.vatReturnId} onChange={(e) => setPaymentForm(p => ({ ...p, vatReturnId: e.target.value }))}>
                <option value="">-- select --</option>
                {returns.map((r) => <option key={r.id} value={r.id}>{periodLabel(r.vatPeriodId)} — {currencySymbol}{Number(r.box5NetVatDue).toFixed(2)}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label>Direction</label>
              <select value={paymentForm.direction} onChange={(e) => setPaymentForm(p => ({ ...p, direction: e.target.value }))}>
                <option value="payment">Payment (we pay tax authority)</option>
                <option value="refund">Refund (tax authority pays us)</option>
              </select>
            </div>
            <div className="form-field"><label>Amount ({currencySymbol}) *</label><input required type="number" step="0.01" value={paymentForm.amount} onChange={(e) => setPaymentForm(p => ({ ...p, amount: e.target.value }))} /></div>
            <div className="form-field"><label>Date *</label><input required type="date" value={paymentForm.paidAt} onChange={(e) => setPaymentForm(p => ({ ...p, paidAt: e.target.value }))} /></div>
            <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Record</button></div>
          </form>
          <div className="table-container" style={{ marginTop: '1.5rem' }}>
            <table className="data-table">
              <thead><tr><th>Return</th><th>Direction</th><th>Amount</th><th>Date</th></tr></thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No payments yet</td></tr>
                ) : payments.map((p) => (
                  <tr key={p.id}>
                    <td>{periodLabel(returns.find(r => r.id === p.vatReturnId)?.vatPeriodId)}</td>
                    <td style={{ textTransform: 'capitalize' }}>{p.direction}</td>
                    <td style={{ fontWeight: 600 }}>{currencySymbol}{Number(p.amount).toFixed(2)}</td>
                    <td>{new Date(p.paidAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </motion.div>
  );
};

export default Vat;